import { AutoComplete, Col, Form, Select, Spin } from "antd";
import "antd/dist/antd.min.css";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getRequest, patchRequest, postRequest } from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import InputField from "../../../common/htmlComponents/InputField";
import "../../../styles/Main.scss";
import { SuccessNotificationMsg } from "../../../utils/Notifications";

const { Option } = Select;

const AddPincode = ({
  handleCancel,
  zoneList,
  setReload,
  pincodeId,
  modalKey,
  setStateM

}) => {
  const { t } = useTranslation();
  const formRef = useRef();
  const [btnLoading, setBtnLoading] = useState(false);
  const [apiLoading, setapiLoading] = useState(false);

// state initialistion
  const initialState = {
    zone_id: "",
    pincode_number: "",
    state_name: "",
    city_name: "",
  };

  const [state, setState] = useState(initialState);

 // Fetch pincode data by ID when component mounts or pincodeId/modalKey changess
  useEffect(() => {
    const pincodeGetById = async () => {
      if (pincodeId !== null) {
        try {
          setapiLoading(true);
          const response = await getRequest(`pincode/${pincodeId}/`);

          const { zone_id, pincode_number, state_name, city_name } = response.data.data;
          setState({ zone_id, pincode_number, state_name, city_name });

          formRef.current.setFieldsValue({ zone_id, pincode_number, state_name, city_name });
          setapiLoading(false);
        } catch (error) {
          setapiLoading(false);
        }
      }
    };
    pincodeGetById();
  }, [pincodeId, modalKey]);

  // Add event listener to handle 'Escape' key press to go back to the list
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        backToList();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

 // Handle input field changes
  const handleInputChange = (e) => {
    let { name, value } = e.target;
    setState({ ...state, [name]: value });
  };
  const [pincodeList, setPincodeList] = useState([]);

    // Search for pincode details and update pincode list
  const searchIN = async (value) => {
    if (value.length === 6) {
      const apiResponse = await getRequest(
        `pincode/?search_pincode_number=${value}`
      );

      if (apiResponse.data.results.success) {
        setPincodeList(apiResponse.data.results.data);
      }
    } else {
      setState((prevState) => ({
        ...prevState,
        state_name: null,
        city_name: null,
        pincode_number: value,
      }));
      formRef.current.setFieldsValue({ state_name: "", city_name: "" });
    }
  };
  const [searchValue, setSearchValue] = useState("");

    // Handle pincode search input and update pincode list
  const onSearch = async (value) => {
    setSearchValue(value);

    value.length >= 4 ? searchIN(!value ? null : value) : setPincodeList([]);
  };


    // Fetch city and state based on pincode and update form fields
  const getCityAndState = async (pincode) => {
    let pincode_number = pincode;
    try {
      const response = await getRequest(
        `pincode/?search_pincode_number=${pincode_number}`
      );
      if (response.data.results.data.length > 0) {

        const { state_name, city_name } = response.data.results.data[0];
        setState((prevState) => ({
          ...prevState,
          state_name: state_name ? state_name : null,
          city_name: city_name ? city_name : null,
          pincode_number: pincode_number,
        }));
        formRef.current.setFieldsValue({ state_name: state_name, city_name: city_name });
      }
      else {
        setState((prevState) => ({
          ...prevState,
          state_name: null,
          city_name: null,
          pincode_number: pincode_number,
        }));
        formRef.current.setFieldsValue({ state_name: "", city_name: "" });
      }
    } catch (error) {
      console.log("Error fetching state and city:", error);
    }
  }

   // Handle change in pincode input and fetch city/state if pincode length is 6
  const handlePincodeChange = async (pincode_number) => {
    if (pincode_number.length === 6) {
      getCityAndState(pincode_number)
    }

  };

  // Reset form and state to initial values, then close the modal
  const backToList = () => {
    setState(initialState);
    formRef.current.setFieldsValue(initialState);
    handleCancel();
  };

  // Submit form data to the API (create or update pincode)
  const handleSubmit = async () => {
    try {
      setBtnLoading(true);
      let response;
      if (pincodeId === null) {
        response = await postRequest("pincode/", state);
      } else {
        response = await patchRequest(`pincode/${pincodeId}/`, state);
      }
      if (response.data.success) {
        SuccessNotificationMsg(
          pincodeId === null
            ? t("pincode_added_successfully")
            : t("pincode_updated_successfully")
        );
        setState(initialState);
        setStateM((prev) => ({
          ...prev,
          page: 1
        }));

        formRef.current.setFieldsValue(initialState);


        setBtnLoading(false);
        handleCancel();
        setReload((reload) => !reload);
      }
    } catch (error) {
      setBtnLoading(false);
    }
  };


  return (
    <>
      <Spin spinning={apiLoading}>
        <Form onFinish={handleSubmit} autoComplete="off" ref={formRef}>
          <Col md={24} className="TextFeild">
            <Form.Item
              name="zone_id"
              label={t("pl_zone")}
              rules={[
                {
                  required: true,
                  message: <Trans>{t("val_zone")}</Trans>,
                },
              ]}
            >
              <Select
                placeholder={t("pl_select_zone")}
                showArrow
                showSearch
                allowClear
                name="zone_id"
                optionFilterProp="children"
                onChange={(value) => setState({ ...state, zone_id: value })}
              >
                {zoneList.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.zone_name.toUpperCase()}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col md={24} className="TextFeild">
            <Form.Item
              name="pincode_number"
              label={t("pl_pincode")}
              rules={[
                {
                  required: true,
                  message: <Trans>{t("val_pincode_select")}</Trans>,
                },
              ]}
            >
              <AutoComplete
                className="autoComplete"
                onSearch={onSearch}
                value={state.pincode_number}
                allowClear
                placeholder={t("pl_pincodeselect")}
                onChange={handlePincodeChange}
                onKeyPress={(e) => {
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault(); // blocks any non-numeric key
                  }
                }}
              >
                {pincodeList.map((option) => (
                  <Option
                    key={option.id}
                    value={option.pincode_number}
                  >
                    {option.pincode_number}
                  </Option>
                ))}
              </AutoComplete>
            </Form.Item>
          </Col>
          <Col md={24} className="TextFeild">
            <InputField
              name="city_name"
              label={t("pl_city")}
              placeholder={t("val_city")}
              value={state.city_name}
              rules={t("val_city")}
              handleInputChange={handleInputChange}
            />
          </Col>
          <Col md={24} className="TextFeild">
            <InputField
              name="state_name"
              label={t("pl_state")}
              placeholder={t("val_state")}
              value={state.state_name}
              rules={t("val_state")}
              handleInputChange={handleInputChange}
            />
          </Col>

          <div className="ModalPopupFooter">
            <ButtonField
              className="CancelGradiantBtn"
              type="primary"
              label={t("cancel")}
              onClick={backToList}
            />
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              label={t("save")}
              htmlType="Submit"
              loading={btnLoading}
            />
          </div>
        </Form>
      </Spin>
    </>
  );
};

AddPincode.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  zoneList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      zone_name: PropTypes.string,
    })
  ),
  setReload: PropTypes.func.isRequired,
  pincodeId: PropTypes.number,
  modalKey: PropTypes.bool,
  setStateM: PropTypes.func
};

export default AddPincode;
